import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import Container from "@/components/Container";
import Logo from "@/components/Logo";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Index" },
  { to: "/about", label: "House" },
  { to: "/process", label: "Method" },
  { to: "/properties", label: "Portfolio" },
  { to: "/before-after", label: "Transformations" },
  { to: "/sell", label: "Sell" },
  { to: "/investors", label: "Partners" },
  { to: "/contact", label: "Contact" },
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.2 });

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <header
      // Mobile: solid opaque background — NO backdrop-blur (kills iOS Safari repaint).
      // Desktop (lg): translucent + backdrop-blur for the premium feel.
      className="fixed top-0 left-0 right-0 z-50 bg-cream-100 lg:bg-cream-100/85 lg:backdrop-blur-xl border-b border-ink-700/8"
      data-testid="public-header"
    >
      <Container className="flex items-center justify-between h-[76px]">
        <Link to="/" data-testid={NAV.logo}>
          <Logo size={26} tone="ink" />
        </Link>

        <nav className="hidden lg:flex items-center">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={NAV.link(l.label.toLowerCase())}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-[0.74rem] tracking-wide font-medium transition-colors ${
                  isActive ? "text-ruby" : "text-ink-700/75 hover:text-ink-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-ruby"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          className="lg:hidden p-2 text-ink-700"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </Container>

      {/* Ruby scroll-progress indicator — desktop only (mobile scroll listeners repaint too heavily) */}
      <motion.div
        className="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-ruby origin-left"
        style={{ scaleX: progress }}
      />

      {open && (
        <div className="lg:hidden bg-cream-100 border-t border-ink-700/10">
          <Container className="py-8 flex flex-col gap-1">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 flex items-baseline gap-3 ${
                    isActive ? "text-ruby" : "text-ink-700"
                  }`
                }
              >
                <span className="font-mono uppercase tracking-[0.18em] text-[0.65rem] text-ink-100">{String(i + 1).padStart(2, "0")}</span>
                <span className="serif-light text-2xl">{l.label}</span>
              </NavLink>
            ))}
          </Container>
        </div>
      )}
    </header>
  );
}
